import type { ChatMessage, DepthMode, EvidenceMode, StreamEvent } from "../shared/types";
import type { ScheduleAccess } from "../shared/scheduling";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
  }
}

async function readError(response: Response): Promise<ApiError> {
  const data: { error?: string; code?: string } = await response.json<{ error?: string; code?: string }>().catch(() => ({}));
  return new ApiError(data.error ?? "The request could not be completed.", response.status, data.code);
}

export async function getPublicConfig(): Promise<{ turnstileSiteKey: string; environment: string; modelLabel: string; visitorCountryCode: string; componentAiEnabled: boolean }> {
  const response = await fetch("/api/config", { headers: { Accept: "application/json" } });
  if (!response.ok) throw await readError(response);
  return response.json();
}

export async function generateAiComponent<T>(component: "executive-summary" | "insights" | "resume" | "footprint", prompt = ""): Promise<T> {
  const response = await fetch("/api/component-ai", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ component, prompt }),
  });
  if (!response.ok) throw await readError(response);
  const result = await response.json<{ data: T }>();
  return result.data;
}

export async function createAiSession(turnstileToken: string): Promise<void> {
  const response = await fetch("/api/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ turnstileToken, acceptedTerms: true }),
  });
  if (!response.ok) throw await readError(response);
}

export async function getScheduleAccess(): Promise<ScheduleAccess> {
  const response = await fetch("/api/schedule", { cache: "no-store" });
  if (!response.ok) throw await readError(response);
  return response.json();
}

export async function unlockSchedule(turnstileToken: string): Promise<ScheduleAccess> {
  const response = await fetch("/api/schedule/access", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ turnstileToken }),
  });
  if (!response.ok) throw await readError(response);
  return response.json();
}

export async function sendChat(
  prompt: string,
  evidenceMode: EvidenceMode,
  depthMode: DepthMode,
  history: ChatMessage[],
  conversationId: string,
  responseMessageId: string,
  onEvent: (event: StreamEvent) => void,
): Promise<void> {
  const response = await fetch("/api/assistant/query", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/x-ndjson" },
    body: JSON.stringify({
      query: prompt,
      evidenceMode,
      depthMode,
      conversationId,
      responseMessageId,
      history: history.slice(-6).map(({ role, content }) => ({ role, content })),
    }),
  });
  if (!response.ok) throw await readError(response);
  if (!response.body) throw new ApiError("The response stream was unavailable.", 502, "stream_unavailable");

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += value;
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      if (line.trim()) onEvent(JSON.parse(line) as StreamEvent);
    }
  }
  if (buffer.trim()) onEvent(JSON.parse(buffer) as StreamEvent);
}

export type FeedbackSentiment = "up" | "down";

export type ResponseInteractionPayload = {
  action: "copy" | "rating";
  feedbackToken: string;
  responseText: string;
  conversation: Array<Pick<ChatMessage, "id" | "role" | "content">>;
  sentiment?: FeedbackSentiment;
  categories?: string[];
  comment?: string;
};

export async function submitResponseInteraction(payload: ResponseInteractionPayload): Promise<void> {
  const response = await fetch("/api/feedback", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await readError(response);
}

export type ContactPayload = {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject: string;
  message: string;
  turnstileToken: string;
  consent: true;
};

export async function submitContact(payload: ContactPayload): Promise<{ id: string }> {
  const response = await fetch("/api/contact", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw await readError(response);
  return response.json();
}
